import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  }
}))

const STATUS_COLORS = {
  draft: 'default',
  ready: 'primary',
  submitted: 'primary',
  approved: 'default',
  revision_requested: 'secondary'
}

const SubmittalList = () => {
  const classes = useStyles()
  const history = useHistory()
  const [submittals, setSubmittals] = useState([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', generalContractor: '', projectId: null, projectName: '' })
  const [projects, setProjects] = useState([])

  const fetchSubmittals = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/submittals')
      setSubmittals(data)
    } catch (err) {
      console.error('Failed to load submittals:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSubmittals() }, [fetchSubmittals])

  const fetchProjects = async () => {
    try {
      const { data } = await axios.get('/api/bid-estimation/projects')
      setProjects(data)
    } catch (err) {
      console.error('Failed to load projects:', err)
    }
  }

  const handleOpenCreate = () => {
    setForm({ name: '', generalContractor: '', architect: '', projectId: null, projectName: '' })
    fetchProjects()
    setCreateOpen(true)
  }

  const handleCreate = async () => {
    if (!form.name.trim()) return
    try {
      const { data } = await axios.post('/api/submittals', form)
      setCreateOpen(false)
      history.push(`/submittals/${data._id}`)
    } catch (err) {
      console.error('Failed to create submittal:', err)
    }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!window.confirm('Delete this submittal?')) return
    try {
      await axios.delete(`/api/submittals/${id}`)
      setSubmittals(prev => prev.filter(s => s._id !== id))
    } catch (err) {
      console.error('Failed to delete submittal:', err)
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5">Submittals</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          New Submittal
        </Button>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>GC</TableCell>
              <TableCell align="center">Items</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Loading...</TableCell>
              </TableRow>
            ) : submittals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No submittals yet. Click "New Submittal" to get started.
                </TableCell>
              </TableRow>
            ) : submittals.map(s => (
              <TableRow
                key={s._id}
                className={classes.clickableRow}
                onClick={() => history.push(`/submittals/${s._id}`)}
              >
                <TableCell><strong>{s.name}</strong></TableCell>
                <TableCell>{s.projectName || '—'}</TableCell>
                <TableCell>{s.generalContractor || '—'}</TableCell>
                <TableCell align="center">{s.itemCount}</TableCell>
                <TableCell>
                  <Chip
                    label={s.status.replace(/_/g, ' ')}
                    color={STATUS_COLORS[s.status] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(s.updatedAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={(e) => handleDelete(e, s._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Submittal</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Project Name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              autoFocus
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="General Contractor"
              value={form.generalContractor}
              onChange={(e) => setForm(prev => ({ ...prev, generalContractor: e.target.value }))}
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="Architect"
              value={form.architect}
              onChange={(e) => setForm(prev => ({ ...prev, architect: e.target.value }))}
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.name || ''}
              onChange={(e, value) => setForm(prev => ({
                ...prev,
                projectId: value ? value._id : null,
                projectName: value ? value.name : ''
              }))}
              renderInput={(params) => (
                <TextField {...params} label="Link to Bid Project (optional)" fullWidth />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} color="primary" variant="contained" disabled={!form.name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SubmittalList
